import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { api, apiPost } from '../lib/api'
import { useAuthStore } from '../stores/authStore'

interface ActiveStore {
    legacyStoreId: string;
    name: string;
    city: string;
    province: string;
}

export function RequestAccessPanel() {
    const { accessToken } = useAuthStore()
    const [selectedStores, setSelectedStores] = useState<string[]>([])
    const [message, setMessage] = useState('')
    const [submitted, setSubmitted] = useState(false)

    const { data: stores, isLoading } = useQuery<ActiveStore[]>({
        queryKey: ['active-stores'],
        queryFn: () => api('/api/admin/stores/active', accessToken)
    })

    const submitMutation = useMutation({
        mutationFn: () => apiPost('/api/admin/access-requests', accessToken, {
            storeIds: selectedStores,
            message: message
        }),
        onSuccess: () => setSubmitted(true)
    })

    const toggleStore = (id: string) => {
        setSelectedStores(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        )
    }

    if (isLoading) return <div>Loading active stores...</div>

    if (submitted) {
        return (
            <div style={{ maxWidth: 600, margin: '4rem auto', textAlign: 'center', padding: '2rem', background: 'var(--color-surface)', borderRadius: 12 }}>
                <h2>✅ Request Submitted</h2>
                <p style={{ color: 'var(--color-text-tertiary)', marginTop: '1rem' }}>
                    Your store access request has been sent. You will be notified when a Store Manager or Administrator reviews your request.
                </p>
                <button
                    className="btn btn-primary"
                    style={{ marginTop: '2rem' }}
                    onClick={() => { setSubmitted(false); setSelectedStores([]); setMessage(''); }}
                >
                    Submit Another Request
                </button>
            </div>
        )
    }

    return (
        <div style={{ maxWidth: 600, margin: '4rem auto', padding: '2rem', background: 'var(--color-surface)', borderRadius: 12 }}>
            <h2 style={{ marginBottom: '1rem' }}>Request Store Access</h2>
            <p style={{ color: 'var(--color-text-tertiary)', marginBottom: '2rem' }}>
                You currently do not have access to any stores. Please select the store(s) you need access to below.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '2rem' }}>
                {stores?.map(store => {
                    const isSelected = selectedStores.includes(store.legacyStoreId)
                    return (
                        <button
                            key={store.legacyStoreId}
                            onClick={() => toggleStore(store.legacyStoreId)}
                            style={{
                                padding: '0.5rem 1rem',
                                borderRadius: '999px',
                                border: `1px solid ${isSelected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                background: isSelected ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                                color: isSelected ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                fontWeight: isSelected ? 500 : 400
                            }}
                        >
                            {store.name} {store.city && <span style={{ opacity: 0.6 }}>({store.city})</span>}
                        </button>
                    )
                })}
            </div>

            <div style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                    Optional Message to Manager
                </label>
                <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="e.g. I was just hired as a keyholder..."
                    style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '1px solid var(--color-border)',
                        background: 'rgba(0,0,0,0.2)',
                        color: 'white',
                        minHeight: 100,
                        resize: 'vertical'
                    }}
                />
            </div>

            <button
                className="btn btn-primary"
                onClick={() => submitMutation.mutate()}
                disabled={selectedStores.length === 0 || submitMutation.isPending}
                style={{ width: '100%' }}
            >
                {submitMutation.isPending ? 'Submitting...' : 'Request Access'}
            </button>
            {submitMutation.isError && (
                <div style={{ color: '#ef4444', marginTop: '1rem', fontSize: '0.875rem', textAlign: 'center' }}>
                    Failed to submit request. Please try again.
                </div>
            )}
        </div>
    )
}
